import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import WorkshopPayModal from "@/components/WorkshopPayModal";
import WorkshopQuoteSheet, { WorkshopPhotoReport } from "@/components/WorkshopQuoteSheet";
import QuoteToExpensesModal from "@/components/QuoteToExpensesModal";
import { ArrowLeft, Printer, Share2, Check, Ban, Wrench, Camera, Trash2, Loader2, DollarSign, Receipt, FileText, Images } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WorkshopQuote() {
  const { id } = useParams();
  const { user } = useAuth();
  const canWrite = ["admin", "auxiliar", "mecanico"].includes(user?.role);
  const [quote, setQuote] = useState(null);
  const [settings, setSettings] = useState(null);
  const [priceMode, setPriceMode] = useState("internal");
  const [doc, setDoc] = useState("cotizacion"); // cotizacion | fotos
  const [uploading, setUploading] = useState(null); // "antes" | "despues"
  const [openPay, setOpenPay] = useState(false);
  const [openConvert, setOpenConvert] = useState(false);
  const antesRef = useRef();
  const despuesRef = useRef();

  const load = useCallback(async () => {
    const [q, s] = await Promise.all([api.get(`/workshop/quotes/${id}`), api.get("/workshop/settings")]);
    setQuote(q.data);
    setSettings(s.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const photoUrl = (fileId) => `${API}/api/files/${fileId}`;

  const doAction = async (action, body) => {
    try {
      await api.post(`/workshop/quotes/${id}/${action}`, body || {});
      toast.success(action === "approve" ? "Cotización aprobada" : action === "reject" ? "Cotización rechazada" : action === "pay" ? "Orden marcada como PAGADA" : "Orden terminada");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const share = async () => {
    try {
      const { data } = await api.post(`/workshop/quotes/${id}/share`);
      const url = `${window.location.origin}/taller/${data.token}`;
      try { await navigator.clipboard.writeText(url); } catch (err) { console.warn("Clipboard no disponible:", err); }
      toast.success("Enlace copiado (válido 30 días)");
      const msg = `Hola, te comparto la orden ${quote.folio} del vehículo ${quote.vehicle?.brand} ${quote.vehicle?.model} (${quote.vehicle?.plates}). Sube las fotos del ANTES y DESPUÉS aquí: ${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al compartir"); }
  };

  const shareClient = async () => {
    try {
      const { data } = await api.post(`/workshop/quotes/${id}/share-client`);
      const url = `${window.location.origin}/cotizacion/${data.token}`;
      try { await navigator.clipboard.writeText(url); } catch (err) { console.warn("Clipboard no disponible:", err); }
      toast.success("Enlace para el cliente copiado (válido 30 días)");
      const msg = `Buen día, le comparto la cotización ${quote.folio} de su vehículo ${quote.vehicle?.brand} ${quote.vehicle?.model} (${quote.vehicle?.plates}). Puede revisarla${quote.status === "borrador" ? " y autorizarla con su firma" : ""} aquí: ${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al compartir"); }
  };

  const uploadPhoto = async (phase, ev) => {
    const files = Array.from(ev.target.files || []);
    ev.target.value = "";
    if (files.length === 0) return;
    setUploading(phase);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post(`/workshop/quotes/${id}/photos`, { file_id: data.id, phase });
      }
      toast.success(`${files.length} foto(s) de ${phase.toUpperCase()} agregadas`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir foto"); }
    finally { setUploading(null); }
  };

  const removePhoto = async (fileId) => {
    if (!window.confirm("¿Eliminar foto?")) return;
    await api.delete(`/workshop/quotes/${id}/photos/${fileId}`);
    load();
  };

  if (!quote || !settings) return <div className="min-h-screen flex items-center justify-center text-slate-400" style={{ background: "var(--bg)" }}>Cargando…</div>;

  return (
    <div className="min-h-screen" style={{ background: "#1e293b" }}>
      <style>{`@media print { .no-print { display: none !important; } body { background: white !important; } }`}</style>
      {/* Toolbar */}
      <div className="no-print sticky top-0 z-20 px-4 py-3 flex flex-wrap items-center gap-2" style={{ background: "#0a0f1d", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <Link to="/workshop" className="text-slate-400 hover:text-cyan-300 flex items-center gap-1 text-xs uppercase tracking-widest font-semibold mr-auto" data-testid="back-to-workshop">
          <ArrowLeft className="w-4 h-4" /> Taller
        </Link>
        {canWrite && quote.status === "borrador" && (
          <>
            <button data-testid="sheet-approve-btn" onClick={() => doAction("approve")} className="px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ background: "var(--green)", color: "#06220f" }}><Check className="w-3.5 h-3.5" /> Aprobar</button>
            <button data-testid="sheet-reject-btn" onClick={() => { const r = window.prompt("Motivo del rechazo (opcional):"); if (r !== null) doAction("reject", { reason: r || null }); }} className="px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ background: "var(--red)", color: "white" }}><Ban className="w-3.5 h-3.5" /> Rechazar</button>
          </>
        )}
        {canWrite && quote.status === "aprobada" && (
          <button data-testid="sheet-complete-btn" onClick={() => doAction("complete")} className="px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ background: "var(--cyan)", color: "#0c1424" }}><Wrench className="w-3.5 h-3.5" /> Terminar</button>
        )}
        {canWrite && ["aprobada", "terminada"].includes(quote.status) && (
          <button data-testid="sheet-pay-btn" onClick={() => setOpenPay(true)} className="px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ background: "#db2777", color: "white" }}><DollarSign className="w-3.5 h-3.5" /> Marcar pagada</button>
        )}
        {canWrite && ["aprobada", "terminada", "pagada"].includes(quote.status) && (quote.items || []).length > 0 && (
          <button data-testid="convert-expenses-btn" onClick={() => setOpenConvert(true)} className="px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ background: "#f59e0b", color: "#1c1006" }}><Receipt className="w-3.5 h-3.5" /> Convertir a gastos</button>
        )}
        {canWrite && quote.status !== "rechazada" && (
          <button data-testid="share-client-btn" onClick={shareClient} className="px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ background: "var(--magenta)", color: "white" }}>
            <Share2 className="w-3.5 h-3.5" /> {quote.status === "borrador" ? "Enviar al cliente (autorizar)" : "Enviar al cliente"}
          </button>
        )}
        {canWrite && ["aprobada", "terminada"].includes(quote.status) && (
          <button data-testid="share-workshop-btn" onClick={share} className="px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ background: "#25D366", color: "#06220f" }}><Share2 className="w-3.5 h-3.5" /> Enviar al mecánico</button>
        )}
        {(quote.photos || []).length > 0 && (
          <div className="flex rounded-sm overflow-hidden" data-testid="doc-toggle" style={{ border: "1px solid #f59e0b" }}>
            {[["cotizacion", "Cotización", FileText], ["fotos", "Reporte fotográfico", Images]].map(([d, l, Icon]) => (
              <button key={d} data-testid={`doc-${d}`} onClick={() => setDoc(d)}
                className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5"
                style={d === doc ? { background: "#f59e0b", color: "#1c1006" } : { background: "transparent", color: "#f59e0b" }}>
                <Icon className="w-3.5 h-3.5" /> {l}
              </button>
            ))}
          </div>
        )}
        <div className="flex rounded-sm overflow-hidden" data-testid="price-mode-toggle" style={{ border: "1px solid #22d3ee" }}>
          {[["internal", "Vista interna"], ["public", "Vista cliente"]].map(([m, l]) => (
            <button key={m} data-testid={`price-mode-${m}`} onClick={() => setPriceMode(m)}
              className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold"
              style={m === priceMode ? { background: "#22d3ee", color: "#0c1424" } : { background: "transparent", color: "#22d3ee" }}>
              {l}
            </button>
          ))}
        </div>
        <button data-testid="print-quote-btn" onClick={() => window.print()} className="px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ background: "white", color: "#0c1424" }}><Printer className="w-3.5 h-3.5" /> Imprimir / PDF</button>
      </div>

      {/* Photo management (internal) */}
      {canWrite && ["aprobada", "terminada"].includes(quote.status) && (
        <div className="no-print max-w-[820px] mx-auto mt-4 px-4">
          <div className="rounded-sm p-3 flex flex-wrap items-center gap-3" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <span className="text-xs uppercase tracking-widest font-semibold text-slate-300 flex items-center gap-1.5"><Camera className="w-4 h-4" /> Reporte fotográfico</span>
            <input ref={antesRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhoto("antes", e)} data-testid="upload-antes-input" />
            <input ref={despuesRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhoto("despues", e)} data-testid="upload-despues-input" />
            <button data-testid="upload-antes-btn" disabled={!!uploading} onClick={() => antesRef.current?.click()} className="px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold disabled:opacity-50" style={{ background: "rgba(220,38,38,0.15)", color: "#fca5a5", border: "1px solid #dc2626" }}>
              {uploading === "antes" ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "+ Fotos ANTES"}
            </button>
            <button data-testid="upload-despues-btn" disabled={!!uploading} onClick={() => despuesRef.current?.click()} className="px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold disabled:opacity-50" style={{ background: "rgba(34,197,94,0.15)", color: "#86efac", border: "1px solid #16a34a" }}>
              {uploading === "despues" ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "+ Fotos DESPUÉS"}
            </button>
            <span className="text-[11px] text-slate-500 ml-auto">O envíale el enlace al mecánico para que las suba desde su celular.</span>
          </div>
          {(quote.photos || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {quote.photos.map((p) => (
                <div key={p.file_id} className="relative group">
                  <img src={photoUrl(p.file_id)} alt={p.phase} className="w-20 h-20 object-cover rounded-sm" style={{ border: `2px solid ${p.phase === "antes" ? "#dc2626" : "#16a34a"}` }} />
                  <button onClick={() => removePhoto(p.file_id)} data-testid={`del-photo-${p.file_id}`} className="absolute -top-1.5 -right-1.5 rounded-full p-0.5 opacity-0 group-hover:opacity-100" style={{ background: "#dc2626", color: "white" }}><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {quote.status === "rechazada" && quote.rejected_reason && (
        <div className="no-print max-w-[820px] mx-auto mt-4 px-4">
          <div className="rounded-sm p-3 text-sm" style={{ background: "rgba(220,38,38,0.12)", border: "1px solid #dc2626", color: "#fca5a5" }}>
            Rechazada por {quote.rejected_by_name}: {quote.rejected_reason}
          </div>
        </div>
      )}

      <div className="py-6 px-2">
        {doc === "fotos"
          ? <WorkshopPhotoReport quote={quote} settings={settings} photoUrl={photoUrl} />
          : <WorkshopQuoteSheet quote={quote} settings={settings} photoUrl={photoUrl} priceMode={priceMode} />}
      </div>

      {openPay && <WorkshopPayModal quote={quote} onClose={() => setOpenPay(false)} onPaid={() => { setOpenPay(false); load(); }} />}
      {openConvert && <QuoteToExpensesModal quote={quote} onClose={() => setOpenConvert(false)} onDone={load} />}
    </div>
  );
}
